import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "sonner";
import {
  approveAiContent,
  fetchAiDrafts,
  rejectAiContent,
} from "../../app/aiContent/aiContent.slice";
import SearchInput from "../../components/SearchInput";
import { getAiItemId, getAiItemSubtitle, getAiItemTitle, isAiItemPremium } from "./helpers";

const headCellSx = {
  fontWeight: 600,
  color: "text.secondary",
  fontSize: 12,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  bgcolor: "background.paper",
};

/**
 * Full-page review screen for AI-generated draft content (decks + cards).
 * Route: /ai-content/drafts
 */
const AiDrafts = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { aiContent } = useSelector((state) => state);

  const [type, setType] = useState("deck"); // 'deck' | 'card'
  // Las cards cuya glosa inglesa coincide con la de una card ya publicada no
  // son un error de la card: es que no se distinguen de otra. Se revisan en su
  // propia pestaña para no ensuciar la cola normal de aprobación.
  const [collisions, setCollisions] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [take, setTake] = useState(20);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const bucket = type === "card" ? aiContent?.cards : aiContent?.decks;
  const items = bucket?.items || [];
  const total = bucket?.pagination?.total ?? bucket?.pagination?.count ?? -1;

  const load = useCallback(
    (overrides = {}) => {
      dispatch(
        fetchAiDrafts({
          type,
          page,
          take,
          search,
          collisions: collisions ? "only" : undefined,
          ...overrides,
        })
      );
    },
    [dispatch, type, page, take, search, collisions]
  );

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, page, take, collisions]);

  const handleChangeType = (_, newType) => {
    if (newType === "collision") {
      setType("card");
      setCollisions(true);
    } else {
      setType(newType);
      setCollisions(false);
    }
    setPage(0);
  };

  const handleSearch = (value) => {
    setSearch(value);
    setPage(0);
    dispatch(fetchAiDrafts({ type, page: 0, take, search: value }));
  };

  const handleApprove = async (item) => {
    const result = await dispatch(approveAiContent({ type, id: getAiItemId(item) }));
    if (approveAiContent.fulfilled.match(result)) {
      toast.success(t("aiDrafts.approvedToast"));
      load();
    } else {
      toast.error(t("toast.errorOccurred"));
    }
  };

  const closeRejectDialog = () => {
    setRejectTarget(null);
    setRejectReason("");
  };

  const handleConfirmReject = async () => {
    const result = await dispatch(
      rejectAiContent({
        type,
        id: getAiItemId(rejectTarget),
        reason: rejectReason?.trim() || undefined,
      })
    );
    if (rejectAiContent.fulfilled.match(result)) {
      toast.success(t("aiDrafts.rejectedToast"));
      closeRejectDialog();
      load();
    } else {
      toast.error(t("toast.errorOccurred"));
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, height: "100%" }}>
      <Paper sx={{ borderRadius: 1, overflow: "hidden", display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
        <Box sx={{ p: 3, pb: 0, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography variant="h6" fontWeight={700}>
              {t("aiDrafts.title")}
            </Typography>
            {total >= 0 && <Chip label={total} size="small" sx={{ fontWeight: 700, fontSize: 13 }} />}
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            <SearchInput
              placeholder={t("aiDrafts.search")}
              value={search}
              onChange={handleSearch}
              onClear={() => handleSearch("")}
              size="medium"
            />
            <Button
              variant="contained"
              disableElevation
              onClick={() => history.push(`/ai-content/review?type=${type}`)}
              sx={{
                whiteSpace: "nowrap",
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "10px",
                py: 1,
                px: 2.5,
              }}
            >
              {t("aiDrafts.reviewOneByOne")} →
            </Button>
          </Stack>
        </Box>
        <Box sx={{ px: 3, flexShrink: 0, borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={collisions ? "collision" : type} onChange={handleChangeType}>
            <Tab
              label={`${t("aiDrafts.decks")} (${aiContent?.decks?.pagination?.total ?? aiContent?.decks?.items?.length ?? 0})`}
              value="deck"
              sx={{ textTransform: "none", fontWeight: 600 }}
            />
            <Tab
              label={`${t("aiDrafts.cards")} (${aiContent?.cards?.pagination?.total ?? aiContent?.cards?.items?.length ?? 0})`}
              value="card"
              sx={{ textTransform: "none", fontWeight: 600 }}
            />
            <Tab
              label={t("aiDrafts.collisions", "Glosa repetida")}
              value="collision"
              sx={{ textTransform: "none", fontWeight: 600 }}
            />
          </Tabs>
        </Box>
        <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>{t("aiDrafts.colTitle")}</TableCell>
                <TableCell sx={headCellSx}>{t("aiDrafts.colInfo")}</TableCell>
                <TableCell sx={headCellSx}>{t("aiDrafts.colStatus")}</TableCell>
                <TableCell sx={headCellSx} align="right">
                  {t("common.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bucket?.isLoading && items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 6, border: 0 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 6, border: 0 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t("aiDrafts.empty")}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item, index) => (
                  <TableRow key={getAiItemId(item) ?? index} hover sx={{ "&:last-child td": { border: 0 } }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={700}>
                        {getAiItemTitle(item)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {getAiItemSubtitle(item, type) || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        <Chip label={t("aiDrafts.draft")} size="small" color="warning" variant="outlined" />
                        <Chip label={t("aiDrafts.ai")} size="small" color="secondary" variant="outlined" />
                        {isAiItemPremium(item) && (
                          <Chip
                            label={`${t("common.premium")}${item?.premium?.cost ? ` · ${item.premium.cost}` : ""}`}
                            size="small"
                            color="info"
                            variant="outlined"
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          disableElevation
                          startIcon={<CheckIcon />}
                          disabled={aiContent?.isActing}
                          onClick={() => handleApprove(item)}
                          sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px" }}
                        >
                          {t("aiDrafts.approve")}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<CloseIcon />}
                          disabled={aiContent?.isActing}
                          onClick={() => setRejectTarget(item)}
                          sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px" }}
                        >
                          {t("aiDrafts.reject")}
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={take}
          onRowsPerPageChange={(e) => {
            setTake(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50]}
          sx={{ flexShrink: 0, borderTop: 1, borderColor: "divider" }}
        />
      </Paper>

      {/* Reject dialog with optional reason */}
      <Dialog open={!!rejectTarget} onClose={closeRejectDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{t("aiDrafts.rejectTitle")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("aiDrafts.confirmReject", { title: getAiItemTitle(rejectTarget) })}
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            label={t("aiDrafts.rejectReason")}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeRejectDialog}>{t("common.cancel")}</Button>
          <Button variant="contained" color="error" disabled={aiContent?.isActing} onClick={handleConfirmReject}>
            {t("aiDrafts.reject")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AiDrafts;
